import { Children, useEffect, useState } from "react";
import { X } from "lucide-react";

/** True once the viewport is at the md breakpoint or wider. */
export const useIsDesktop = () => {
  const [desktop, setDesktop] = useState(() =>
    typeof window === "undefined" ? true : window.matchMedia("(min-width: 768px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => setDesktop(e.matches);
    mq.addEventListener("change", onChange);
    setDesktop(mq.matches);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return desktop;
};

export const Modal = ({ open, onClose, title, subtitle, children, testid, wide = false }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-slate-900/50 p-0 sm:p-8" data-testid={testid}>
      <div className={`w-full ${wide ? "max-w-4xl" : "max-w-2xl"} min-h-full sm:min-h-0 rounded-none sm:rounded-lg bg-white border-0 sm:border border-slate-200 fade-up`}>
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4 sm:px-6 sm:py-5 sm:rounded-t-lg">
          <div>
            <h3 className="font-ui text-base sm:text-lg font-bold tracking-tight">{title}</h3>
            {subtitle && <p className="text-[13px] sm:text-sm text-slate-500 mt-1.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} data-testid="modal-close-btn" aria-label="Close"
            className="-mr-1 -mt-1 grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-900">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 sm:p-6 pb-8">{children}</div>
      </div>
    </div>
  );
};

export const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-3 sm:py-2.5 text-[15px] sm:text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10";
export const labelCls = "text-[12px] font-semibold uppercase tracking-wider text-slate-700";
export const primaryBtnCls = "inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] px-6 py-3 sm:py-2.5 text-sm font-medium text-white hover:bg-[#1E293B] hover:text-white disabled:opacity-60";
export const ghostBtnCls = "inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 sm:py-2 text-sm font-medium text-slate-700 hover:border-slate-900 hover:text-slate-900 disabled:opacity-60";

export const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className={labelCls}>{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

export const EnterKeyHandler = () => {
  // Global handler: pressing Enter on an input/select/textarea moves to the next logical field.
  // Skip textareas and implicit submit buttons.
  useEffect(() => {
      const handler = (e) => {
      if (e.key !== "Enter") return;
      const el = document.activeElement;
      if (!el) return;
      const tag = el.tagName?.toLowerCase();
      if (tag === "textarea") return; // preserve newline behaviour
      const type = el.getAttribute("type") || "";
      if (type === "submit" || type === "button") return;
      // Only handle when focus is inside an input/select/textarea
        if (!/input|select|textarea/.test(tag)) return;
      // Don't act if element has data-enter-next="false"
      if (el.dataset && el.dataset.enterNext === "false") return;
      e.preventDefault();
        const focusable = Array.from(document.querySelectorAll(
        'input:not([disabled]):not([readonly]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]):not([readonly])'
      ));
      const idx = focusable.indexOf(el);
      if (idx === -1) return;
      // Find next focusable that is visible and not readOnly
      for (let i = idx + 1; i < focusable.length; i++) {
        const next = focusable[i];
        if (!next.offsetParent) continue; // hidden
        if (next.hasAttribute('readonly')) continue;
        try { next.focus(); break; } catch (_) { continue; }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);
  return null;
};

export const Table = ({ columns, rows, renderRow, empty, testid }) => {
  const desktop = useIsDesktop();
  if (rows.length === 0) {
    return <div className="panel overflow-hidden" data-testid={testid}>{empty}</div>;
  }
  return (
    <div className="panel overflow-hidden" data-testid={testid}>
      {desktop ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                {columns.map((c, i) => (
                  <th key={`${c}-${i}`} className="px-5 py-4 font-semibold whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">{rows.map(renderRow)}</tbody>
          </table>
        </div>
      ) : (
        /* Mobile: the same row, rendered as a readable stacked card (single DOM tree, no duplicate ids) */
        <div className="divide-y divide-slate-100">
          {rows.map((row, rowIndex) => {
            const tr = renderRow(row);
            const cells = Children.toArray(tr.props.children);
            return (
              <div key={tr.key || rowIndex} data-testid={tr.props["data-testid"]} className="p-4 space-y-2.5">
                {cells.map((cell, i) => {
                  const heading = columns[i];
                  const content = cell?.props?.children;
                  if (content === null || content === undefined || content === "") return null;
                  if (!heading) {
                    return <div key={i} className="pt-1.5" data-testid={cell.props["data-testid"]}>{content}</div>;
                  }
                  return (
                    <div key={i} className="flex items-start justify-between gap-4">
                      <span className="shrink-0 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 pt-0.5">
                        {heading}
                      </span>
                      <span className="min-w-0 text-right text-sm text-slate-800 break-words"
                        data-testid={cell.props["data-testid"]}>{content}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const Td = ({ children, className = "", ...rest }) => (
  <td className={`px-5 py-4 ${className}`} {...rest}>{children}</td>
);

export function useToggle(initial = false) {
  const [open, setOpen] = useState(initial);
  return [open, () => setOpen(true), () => setOpen(false)];
}
